import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { bool, node, func, string, number, oneOfType } from 'prop-types';
import { isNil } from 'lodash';
import { Checkbox, Icon } from '@fiverr-private/fit';
import { r_check } from '@fiverr-private/icons';
import useMenuContext from '../useMenuContext';
import useMenuGroupContext from '../MenuGroup/useMenuGroupContext';

import styles from './index.module.scss';

const FIRST = 1;
const SECOND = 2;
const THIRD = 3;

const MenuItem = ({
    className,
    children,
    onClick = Function.prototype,
    icon,
    name,
    value,
    selectable: itemSelectable
}) => {
    const menuItemRef = useRef();
    const {
        close,
        selectable: menuSelectable,
        hover,
        setHover = Function.prototype,
        addMenuItem = Function.prototype,
        removeMenuItem = Function.prototype
    } =  useMenuContext();
    const { checked, setChecked = Function.prototype, isMulti = false, shouldCloseOnClick = true } = useMenuGroupContext();
    const selectable = typeof itemSelectable !== 'undefined' ? itemSelectable : menuSelectable;

    const handleClick = useCallback((event) => {
        if (selectable){
            setChecked(value);
        }

        if (shouldCloseOnClick) {
            close();
        }

        onClick(event);
    }, [onClick, setChecked, close, selectable, shouldCloseOnClick, value]);

    useEffect(() => {
        addMenuItem(menuItemRef, handleClick);

        return () => {
            removeMenuItem(menuItemRef);
        };
    }, [removeMenuItem, addMenuItem, menuItemRef, handleClick]);

    const isItemChecked = useMemo(() => {
        if (!selectable || isNil(checked)){
            return false;
        }

        if (isMulti) {
            return checked.includes(value);
        }

        return checked === value;
    }, [value, selectable, isMulti, checked]);

    const getCheckedDisplay = () => {
        if (isMulti) {
            return (
                <Checkbox className="m-r-4"
                    name={name}
                    checked={isItemChecked}
                    onClick={(event) => event.stopPropagation()/* prevents `handleClick` being called twice */}/>
            );
        }

        return (
            <label className="m-r-12">
                <input name={name} value={value} type="radio" hidden/>
                {<Icon size={11} className={styles.check}>{isItemChecked ? r_check : null }</Icon>}
            </label>
        );
    };

    const onMouseEnter = useCallback(() => {
        menuItemRef.current.focus();
        setHover(menuItemRef);
    }, [setHover, menuItemRef]);

    return (
        <li onClick={handleClick} onMouseEnter={onMouseEnter} ref={menuItemRef}
            className={classNames(
                'p-t-8 p-b-8 p-l-16 p-r-16 flex flex-row flex-items-center',
                styles.container,
                { [styles.hover]: hover === menuItemRef },
                className)}>
            { icon && (
                <div style={{ order: selectable ? THIRD : FIRST, flex: selectable ? 1 : 0 }}
                    className={classNames('flex flex-items-center', { 'm-r-12': !selectable, 'm-l-12 flex-justify-end': selectable })}>
                    <Icon className={styles.icon}>{icon}</Icon>
                </div>
            )}
            <div style={{ order: SECOND }} className={styles.content}>{children}</div>
            { selectable && <div className="flex flex-items-center" style={{ order: FIRST }}>
                {getCheckedDisplay()}
            </div>}
        </li>
    );
};

MenuItem.propTypes = {
    /**
     * The item content
     */
    children: node.isRequired,
    /**
     * Icon to be displayed
     */
    icon: node,
    /**
     * onClick event handler
     */
    onClick: func,
    /**
    * Custom classes you wish to pass to the component.
    */
    className: string,
    /**
     * A name in case the item is under selectable group and being used as a form input
     */
    name: string,
    /**
     * A name in case the item is under selectable group and being used as a form input
     */
    value: oneOfType([string, number]),
    /**
     * Whether the item is selectable. It overrides the higher-level `selectable` prop
     */
    selectable: bool
};

export default MenuItem;
